import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/motion";
import { ARTICLES } from "@/data/content";

const ArticleMeta = ({ article, dark = false }) => (
  <p className={`font-mono text-[11px] uppercase tracking-[0.25em] ${dark ? "text-[#A3A299]" : "text-muted"}`}>
    {article.category} · {article.date} · {article.readTime}
  </p>
);

const Viden = () => {
  const [featured, ...rest] = ARTICLES;

  return (
    <main data-testid="page-viden">
      <PageHero
        testId="hero-viden"
        overline="Viden"
        title={["Viden &", "perspektiver."]}
        lead="Et redaktionelt magasin om hjernens selvregulering, neuroplasticitet, søvn, stress, fokus og performance, skrevet til mennesker, ikke til fagfolk."
      />

      <section className="border-b border-hairline">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
          <Reveal>
            <Link
              to={`/viden/${featured.slug}`}
              data-testid={`viden-featured-${featured.slug}`}
              className="group grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16"
            >
              <div className="lg:col-span-7">
                <div className="relative aspect-[16/10] overflow-hidden border border-sand">
                  <img
                    src={featured.image}
                    alt={featured.title}
                    className="img-editorial-soft absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                  />
                </div>
              </div>
              <div className="flex flex-col justify-center lg:col-span-5">
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted">Seneste artikel</p>
                <div className="mt-5">
                  <ArticleMeta article={featured} />
                </div>
                <h2 className="mt-4 font-serif text-3xl leading-[1.1] tracking-tight text-charcoal transition-colors sm:text-4xl lg:text-5xl">
                  {featured.title}
                </h2>
                <p className="mt-5 text-base leading-relaxed text-graphite">{featured.excerpt}</p>
                <span className="mt-8 inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-charcoal">
                  <span className="border-b border-charcoal pb-1">Læs artiklen</span>
                  <ArrowUpRight
                    className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    strokeWidth={1.5}
                  />
                </span>
              </div>
            </Link>
          </Reveal>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted">Arkivet</p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-2">
            {rest.map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.06}>
                <Link to={`/viden/${a.slug}`} data-testid={`viden-article-${a.slug}`} className="group block">
                  <div className="relative aspect-[16/10] overflow-hidden border border-sand">
                    <img
                      src={a.image}
                      alt={a.title}
                      loading="lazy"
                      className="img-editorial-soft absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                    />
                  </div>
                  <div className="mt-6">
                    <ArticleMeta article={a} />
                    <h3 className="mt-3 font-serif text-2xl leading-[1.15] tracking-tight text-charcoal sm:text-3xl">
                      {a.title}
                    </h3>
                    <p className="mt-3 max-w-xl text-sm leading-relaxed text-graphite">{a.excerpt}</p>
                    <span className="mt-5 inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-charcoal">
                      <span className="border-b border-charcoal pb-1">Læs artiklen</span>
                      <ArrowUpRight
                        className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                        strokeWidth={1.5}
                      />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-16">
            <p className="max-w-2xl text-sm leading-relaxed text-muted">
              Flere artikler er på vej om hjernens selvregulering, nervesystemet, ny forskning og
              hjernetræning i hverdagen.
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Viden;
